import { useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { KubesoloHostResult, KubesoloTest } from '@api/types.gen';

import { Button, LoadingButton } from '@@/buttons';
import { SearchBar } from '@@/datatables/SearchBar';
import { Modal } from '@@/modals/Modal';
import { Color } from '@@/StatusSummaryBar/FilterBarButton';
import { StatusSummaryBar } from '@@/StatusSummaryBar/StatusSummaryBar';

import { formatHost } from '../parseMachines';
import { useRetestMutation } from '../queries/useRetestMutation';

import { FailureGroups } from './FailureGroups';
import { HostList } from './HostList';
import { InProgressSection } from './InProgressSection';
import { groupHosts, groupLabels, HostGroup, hostGroups } from './groupHosts';

const groupColors: Record<HostGroup, Color> = {
  attention: 'error',
  running: 'blue',
  queued: 'gray',
  passed: 'success',
  alreadyInstalled: 'warning',
};

interface Props {
  test: KubesoloTest;
  onDismiss(): void;
}

/** Every machine's result, kept apart from the form so a long list doesn't push the inputs away. */
export function ConnectionTestDetails({ test, onDismiss }: Props) {
  // undefined until the user picks a group
  const [filter, setFilter] = useState<HostGroup | null>();
  const [search, setSearch] = useState('');
  const retestMutation = useRetestMutation();
  const groups = groupHosts(test.hosts);
  const failedIds = groups.attention.map((host) => host.id);
  const defaultFilter = failedIds.length > 0 ? 'attention' : null;
  // a group that empties loses its count, so it can't stay selected
  const isFilterShown =
    filter === null || (!!filter && groups[filter].length > 0);
  const selected = isFilterShown ? filter : defaultFilter;
  const shown = groupHosts(
    test.hosts.filter((host) => matchesSearch(host, search))
  );
  // remount on search, so the page starts over like a datatable's
  const listKey = `${selected ?? 'total'}-${search}`;

  return (
    <Modal onDismiss={onDismiss} aria-label="Connection test" size="xl">
      <Modal.Header title="Connection test" />
      <Modal.Body>
        <div className="mb-3">
          <StatusSummaryBar
            total={test.hosts.length}
            segments={hostGroups.map((group) => ({
              key: group,
              label: groupLabels[group],
              count: groups[group].length,
              color: groupColors[group],
            }))}
            value={selected}
            onChange={setFilter}
            radioGroupName="kubesolo-test-filter"
            ariaLabel="Filter machines by result"
            data-cy="kubesolo-test-summary"
            rightSlot={
              <SearchBar
                value={search}
                onChange={setSearch}
                data-cy="kubesolo-test-search"
              />
            }
          />
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {selected === 'attention' ? (
            <FailureGroups key={listKey} hosts={shown.attention} test={test} />
          ) : (
            <HostList
              key={listKey}
              label={selected ? groupLabels[selected] : 'All machines'}
              hosts={
                selected
                  ? shown[selected]
                  : hostGroups.flatMap((group) => shown[group])
              }
              test={test}
            />
          )}
          {selected === 'attention' && (
            <InProgressSection
              running={shown.running}
              queued={shown.queued}
              test={test}
            />
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {failedIds.length > 0 && (
          <LoadingButton
            color="primary"
            icon={RefreshCw}
            isLoading={retestMutation.isLoading}
            loadingText="Starting..."
            onClick={() =>
              retestMutation.mutate({ testId: test.id, hostIds: failedIds })
            }
            type="button"
            data-cy="kubesolo-retest-failed-button"
          >
            Re-test failed machines
          </LoadingButton>
        )}
        <Button
          color="default"
          onClick={onDismiss}
          data-cy="kubesolo-test-continue-button"
        >
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function matchesSearch(host: KubesoloHostResult, search: string) {
  const query = search.toLowerCase();
  return [formatHost(host), host.hostname ?? ''].some((text) =>
    text.toLowerCase().includes(query)
  );
}
